import { isCloudBearerAuthFailure } from "../../lib/cloudAuthFailure";
import {
  CMMD_STARTUP_AUTH_RETRY_DELAY_MS,
  resolveCmmdStartupAuthFailureDecision,
} from "../../startup/startupReadiness";
import { prepareSseReader } from "../../cmmd/cmmdForgeRuntimeListTransportInternals";
import { readEnvironmentListPayload } from "./cmmdEnvironmentListContracts";
import type { CmmdEnvironmentPickerEntry } from "./cmmdEnvironmentListTypes";

const CMMD_ENVIRONMENT_LIST_URL = "/api/cmmd/environments";
const CMMD_ENVIRONMENT_LIST_EVENTS_URL = "/api/cmmd/environments/events";
const CMMD_ENVIRONMENT_LIST_REQUEST_TIMEOUT_MS = 15_000;

const POLL_INTERVAL_MS = 30_000;
const SSE_RECONNECT_BASE_MS = 2_000;
const SSE_RECONNECT_CAP_MS = 30_000;
const REFRESH_COALESCE_MS = 200;

export type CmmdEnvironmentListTransportEvent =
  | { readonly kind: "loading" }
  | {
      readonly kind: "environments";
      readonly environments: readonly CmmdEnvironmentPickerEntry[];
      readonly fetchedAt: string;
    }
  | { readonly kind: "failed"; readonly message: string }
  | { readonly kind: "auth-refreshing" }
  | { readonly kind: "auth-required"; readonly message: string };

export interface CmmdEnvironmentListTransport {
  readonly initialize: () => void;
  readonly refresh: () => void;
  readonly teardown: () => void;
}

export interface CmmdEnvironmentListTransportOptions {
  readonly onEvent: (event: CmmdEnvironmentListTransportEvent) => void;
}

type ListRequestResult =
  | { readonly kind: "ok"; readonly payload: readonly CmmdEnvironmentPickerEntry[] }
  | { readonly kind: "auth" }
  | { readonly kind: "timed-out" }
  | { readonly kind: "failed"; readonly message: string };

interface SseFrame {
  readonly name: string;
  readonly data: string | null;
}

/** One request with its timeout, settled into a result: network and auth
 * failures are classified here so the caller only decides what to emit. */
async function runListRequest(): Promise<ListRequestResult> {
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  try {
    const request = requestEnvironmentList(CMMD_ENVIRONMENT_LIST_URL, controller);
    const timeout = new Promise<ListRequestResult>((resolve) => {
      timeoutId = setTimeout(() => {
        controller.abort();
        resolve({ kind: "timed-out" });
      }, CMMD_ENVIRONMENT_LIST_REQUEST_TIMEOUT_MS);
    });
    return await Promise.race([request, timeout]);
  } catch (error) {
    if (isCloudBearerAuthFailure(error)) {
      return { kind: "auth" };
    }
    return {
      kind: "failed",
      message: error instanceof Error ? error.message : "Failed to load environments.",
    };
  } finally {
    if (timeoutId !== null) clearTimeout(timeoutId);
  }
}

/** Reads SSE chunks until the stream ends, the signal aborts, or a frame
 * handler returns true (a terminal frame). */
async function readEnvironmentSseFrames(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  signal: AbortSignal,
  handleFrame: (frame: SseFrame) => boolean,
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = "";
  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (signal.aborted || done) return;
    const messages = `${buffer}${decoder.decode(value, { stream: true })}`.split(/\n\n/);
    buffer = messages.pop() ?? "";
    for (const message of messages) {
      const frame = parseSseFrame(message);
      if (frame === null) continue;
      if (handleFrame(frame)) return;
    }
  }
}

/** One GET of the unioned list, settled into a result the transport applies. */
async function requestEnvironmentList(
  url: string,
  controller: AbortController,
): Promise<ListRequestResult> {
  const response = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal: controller.signal,
  });
  if (response.status === 401 || response.status === 403) {
    return { kind: "auth" };
  }
  if (!response.ok) {
    return { kind: "failed", message: `Request failed (${response.status}).` };
  }
  const payload = readEnvironmentListPayload(await response.json());
  return { kind: "ok", payload };
}

/** Nulls a timer handle in place: clearTimeout plus the null the state slots
 * store. */
function clearTimer(timer: ReturnType<typeof setTimeout> | null): null {
  if (timer !== null) clearTimeout(timer);
  return null;
}

/** One SSE message, split into its event name and data line. Null for
 * keepalive comments and anything without both fields. */
function parseSseFrame(message: string): SseFrame | null {
  let name = "";
  let data: string | null = null;
  for (const line of message.split("\n")) {
    if (line.startsWith("event: ")) name = line.slice(7).trim();
    if (line.startsWith("data: ")) data = line.slice(6).trim();
  }
  if (name === "" || data === null) return null;
  return { name, data };
}

/**
 * Live transport for the unioned environment list: one GET on initialize, then
 * the CMMD SSE stream with reconnect and a 30 s poll fallback, the same shape
 * as the cloud catalog transport. Payload validation lives in
 * `cmmdEnvironmentListContracts`, so a malformed frame cannot reach the store.
 */
export function createCmmdEnvironmentListTransport({
  onEvent,
}: CmmdEnvironmentListTransportOptions): CmmdEnvironmentListTransport {
  let initialized = false;
  let stopped = false;
  let usingPollFallback = false;
  let authRequired = false;
  let authFailureAttempt = 0;
  let authRetryTimer: ReturnType<typeof setTimeout> | null = null;
  let sseAttempt = 0;
  let sseAbort: AbortController | null = null;
  let listGeneration = 0;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  let sseReconnectTimer: ReturnType<typeof setTimeout> | null = null;

  function clearBackgroundTimers(): void {
    pollTimer = clearTimer(pollTimer);
    sseReconnectTimer = clearTimer(sseReconnectTimer);
  }

  function stopTransport(): void {
    sseAbort?.abort();
    sseAbort = null;
    clearBackgroundTimers();
    authRetryTimer = clearTimer(authRetryTimer);
  }

  function isActive(): boolean {
    return !stopped && !authRequired;
  }

  function raiseAuthRequired(message: string): void {
    if (authRequired || stopped) return;
    authRequired = true;
    stopTransport();
    onEvent({ kind: "auth-required", message });
  }

  function handleAuthFailure(message: string): void {
    if (stopped || authRequired) return;
    authFailureAttempt += 1;
    const decision = resolveCmmdStartupAuthFailureDecision({
      failedAttempt: authFailureAttempt,
      retryDelayMs: CMMD_STARTUP_AUTH_RETRY_DELAY_MS,
    });
    if (decision.kind === "terminal") {
      raiseAuthRequired(message);
      return;
    }
    onEvent({ kind: "auth-refreshing" });
    authRetryTimer = clearTimer(authRetryTimer);
    authRetryTimer = setTimeout(() => {
      authRetryTimer = null;
      void listAndResume();
    }, decision.delayMs);
  }

  function publishEnvironments(environments: readonly CmmdEnvironmentPickerEntry[]): void {
    authFailureAttempt = 0;
    onEvent({ kind: "environments", environments, fetchedAt: new Date().toISOString() });
  }

  /** The one place a settled list request becomes store events. */
  function applyListResult(result: ListRequestResult): void {
    if (result.kind === "ok") {
      publishEnvironments(result.payload);
      return;
    }
    if (result.kind === "auth") {
      handleAuthFailure("CMMD authentication required. Sign in again to continue.");
      return;
    }
    if (result.kind === "timed-out") {
      onEvent({ kind: "failed", message: "Environment list refresh timed out." });
      return;
    }
    onEvent({ kind: "failed", message: result.message });
  }

  function fetchEnvironments(): Promise<void> {
    if (!isActive()) return Promise.resolve();
    const generation = ++listGeneration;
    onEvent({ kind: "loading" });
    return runListRequest().then((result) => {
      if (!isActive() || generation !== listGeneration) return;
      applyListResult(result);
    });
  }

  function schedulePoll(): void {
    if (!isActive()) return;
    pollTimer = clearTimer(pollTimer);
    pollTimer = setTimeout(async () => {
      await fetchEnvironments();
      if (isActive()) schedulePoll();
    }, POLL_INTERVAL_MS);
  }

  function scheduleSseReconnect(attempt: number): void {
    sseAttempt = attempt + 1;
    const delay = Math.min(SSE_RECONNECT_BASE_MS * 2 ** attempt, SSE_RECONNECT_CAP_MS);
    sseReconnectTimer = clearTimer(sseReconnectTimer);
    sseReconnectTimer = setTimeout(() => {
      sseReconnectTimer = null;
      void startSse(sseAttempt);
    }, delay);
  }

  /** The reader ended: hand the lifecycle to the next stream when this
   * controller still owns it. A stale continuation after a refresh or
   * teardown schedules nothing, and an auth-required frame leaves the resume
   * to the auth retry timer. */
  function continueFromCompletedStream(controller: AbortController, attempt: number): void {
    const ownedSlot = sseAbort === controller;
    const wasAborted = controller.signal.aborted;
    // A terminal frame can stop the reader while the response remains open.
    controller.abort();
    if (ownedSlot) sseAbort = null;
    if (wasAborted || !ownedSlot || !isActive() || authRetryTimer !== null) return;
    scheduleSseReconnect(attempt);
  }

  /** The stream errored: fall back to polling when this controller still owns
   * the lifecycle; otherwise its owner already replaced or stopped it. */
  function recoverFromBrokenStream(controller: AbortController): void {
    if (controller.signal.aborted || sseAbort !== controller) return;
    sseAbort = null;
    usingPollFallback = true;
    schedulePoll();
  }

  async function startSse(attempt: number): Promise<void> {
    if (!isActive() || sseAbort !== null) return;
    sseAbort = new AbortController();
    const controller = sseAbort;
    try {
      const response = await fetch(CMMD_ENVIRONMENT_LIST_EVENTS_URL, {
        headers: { Accept: "text/event-stream" },
        signal: controller.signal,
        credentials: "include",
      });
      const sseReader = await prepareSseReader(response);
      if (sseAbort !== controller) return;
      if (typeof sseReader === "string") {
        sseAbort = null;
        if (sseReader === "auth-required") {
          handleAuthFailure("CMMD authentication required. Sign in again to continue.");
          return;
        }
        // SSE unsupported upstream: fall back to polling.
        usingPollFallback = true;
        schedulePoll();
        return;
      }
      await consumeEnvironmentFrames(sseReader, controller.signal);
      continueFromCompletedStream(controller, sseAttempt === 0 ? 0 : attempt);
    } catch {
      recoverFromBrokenStream(controller);
    }
  }

  function consumeEnvironmentFrames(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    signal: AbortSignal,
  ): Promise<void> {
    const handleFrame = (frame: SseFrame): boolean => {
      // A teardown, a refresh, or a raised auth requirement invalidates every
      // frame after it: stop the batch instead of publishing a stale snapshot
      // over the lifecycle that replaced this one.
      if (signal.aborted || !isActive()) return true;
      if (frame.name === "auth-required") {
        handleAuthFailure("CMMD authentication required. Sign in again to continue.");
        return true;
      }
      if (frame.name !== "environments" || frame.data === null) return false;
      try {
        const environments = readEnvironmentListPayload(JSON.parse(frame.data));
        sseAttempt = 0;
        publishEnvironments(environments);
      } catch {
        // A malformed frame is skipped; the next tick replaces the snapshot.
      }
      return false;
    };
    return readEnvironmentSseFrames(reader, signal, handleFrame);
  }

  function listAndResume(): Promise<void> {
    return fetchEnvironments().then(() => {
      if (!isActive()) return;
      if (usingPollFallback) schedulePoll();
      else void startSse(sseAttempt);
    });
  }

  return {
    initialize(): void {
      if (initialized || stopped) return;
      initialized = true;
      void listAndResume();
    },
    refresh(): void {
      if (stopped) return;
      refreshTimer = clearTimer(refreshTimer);
      refreshTimer = setTimeout(() => {
        refreshTimer = null;
        stopTransport();
        usingPollFallback = false;
        authRequired = false;
        authFailureAttempt = 0;
        void listAndResume();
      }, REFRESH_COALESCE_MS);
    },
    teardown(): void {
      stopped = true;
      refreshTimer = clearTimer(refreshTimer);
      stopTransport();
    },
  };
}
